/**
 * The elevation drawing's SVG edge projections: fetch, cache, restyle, render.
 *
 * Extracted from `RenderWallElevation` unchanged, for one reason — a SCHEMATIC
 * needs to draw one. `HungLightSchematic` draws a pendant's canopy and shade
 * from CROPS of the fixture's own projection, and a schematic importing from
 * the renderer would close an import cycle (the renderer imports the registry,
 * which imports every schematic).
 *
 * `crop` is the only addition: it narrows the rendered SVG to a horizontal
 * band of itself by rewriting the root `viewBox`, so a caller can draw one
 * PART of a fixture. See `HungLightSchematic` for why that is worth doing.
 */
import React, { useState, useEffect } from 'react';
import { HIGHLIGHT_COLORS } from '../../constants/highlightColors';
import { recordSvgFetch, recordSvgProcessing } from './ElevationStats';

const svgCache = new Map();
// Cache for processed SVG content (after regex transformations)
// Key format: `${url}:${strokeWidthPx}`
const processedSvgCache = new Map();

/**
 * Pre-fetch SVG URLs into the module-level cache so they're instantly available
 * when InlineSVGProjection components mount during elevation rendering.
 * Call this before rendering elevations to eliminate network wait during render.
 *
 * @param {string[]} urls - Array of SVG URLs to pre-fetch
 */
export const prefetchSvgUrls = async (urls) => {
  const uniqueUrls = [...new Set(urls)].filter((u) => u && !svgCache.has(u));
  if (uniqueUrls.length === 0) return;

  await Promise.all(
    uniqueUrls.map(async (url) => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          const text = await response.text();
          svgCache.set(url, text);
        }
      } catch (e) {
        // Silently skip — InlineSVGProjection will retry on mount
      }
    })
  );
};

/**
 * Process raw SVG content with non-scaling-stroke and uniform stroke width.
 * This function is expensive due to multiple regex operations, so results are cached.
 */
const processSvgContent = (rawSvg, strokeWidthPx, url) => {
  const cacheKey = `${url}:${strokeWidthPx}`;

  // Check processed cache first
  if (processedSvgCache.has(cacheKey)) {
    return processedSvgCache.get(cacheKey);
  }

  const processStart = performance.now();
  let finalSvg = rawSvg;

  // First, replace any existing vector-effect attributes to avoid duplicates
  finalSvg = finalSvg.replace(
    /vector-effect="[^"]*"/g,
    'vector-effect="non-scaling-stroke"'
  );

  // Add vector-effect to all common SVG elements that can have strokes (if not already present)
  const strokeElements = [
    'g',
    'line',
    'path',
    'rect',
    'circle',
    'ellipse',
    'polygon',
    'polyline',
  ];
  strokeElements.forEach((tag) => {
    // Match elements that don't already have vector-effect
    finalSvg = finalSvg.replace(
      new RegExp(`<${tag}((?![^>]*vector-effect)[^>]*?)/?>`, 'g'),
      (match, attrs) =>
        `<${tag}${attrs} vector-effect="non-scaling-stroke"${match.endsWith('/>') ? '/>' : '>'}`
    );
  });

  // Set uniform stroke width in pixels
  finalSvg = finalSvg.replace(
    /stroke-width="[^"]*"/g,
    `stroke-width="${strokeWidthPx}"`
  );

  // Force SVG to fill container and center content
  // Remove any existing width/height/preserveAspectRatio/style attributes on the svg element
  finalSvg = finalSvg
    .replace(/<svg([^>]*)\swidth="[^"]*"/g, '<svg$1')
    .replace(/<svg([^>]*)\sheight="[^"]*"/g, '<svg$1')
    .replace(/<svg([^>]*)\spreserveAspectRatio="[^"]*"/g, '<svg$1')
    .replace(/<svg([^>]*)\sstyle="[^"]*"/g, '<svg$1');
  // Add width="100%" height="100%", preserveAspectRatio for centering, overflow visible, and display:block
  finalSvg = finalSvg.replace(
    /<svg([^>]*)>/,
    '<svg$1 width="100%" height="100%" preserveAspectRatio="xMidYMid meet" overflow="visible" style="display:block">'
  );

  // Record processing time
  const processDuration = performance.now() - processStart;
  recordSvgProcessing(url, processDuration);

  // Cache the processed result
  processedSvgCache.set(cacheKey, finalSvg);

  return finalSvg;
};

// A crop fraction this close to 0 or 1 is the artwork's own edge.
const EDGE_EPS = 1e-6;

/**
 * The rectangle a band is CUT to, in viewBox units: the band's own top and
 * bottom where a neighbouring band takes over, and well past the artwork
 * wherever the band is the artwork's edge.
 *
 * A stroke is centred on its path, and the viewBox is the model's bounding
 * box, so every outermost line — a shade's bottom rim, a canopy's top, the
 * widest band's sides — lies exactly ON the box edge. Cut there and half of
 * it is lost, and the half-width line that is left disappears outright once a
 * PDF capture rasterises and compresses it (the pendant shades printed with
 * no bottom). Between bands the cut is right: the neighbour draws the other
 * half. Past the edge there is no geometry, only that spilled stroke, so the
 * clip can reach a whole artwork beyond it and never admit anything else.
 */
const bandClipRect = ({ x, y, w, h }, crop) => {
  const startsAtEdge = (crop.top || 0) <= EDGE_EPS;
  const endsAtEdge = (crop.top || 0) + crop.height >= 1 - EDGE_EPS;
  const top = startsAtEdge ? y - h : y + h * (crop.top || 0);
  const bottom = endsAtEdge
    ? y + 2 * h
    : y + h * ((crop.top || 0) + crop.height);
  return { x: x - w, y: top, width: 3 * w, height: bottom - top };
};

/**
 * Narrow a processed SVG to a horizontal BAND of itself, by fraction of its
 * own viewBox — `{top: 0.7, height: 0.3}` is its bottom third.
 *
 * The projection's viewBox IS the model's bounding box (verified across every
 * hung fixture in the catalog: 28/28), so a band of the viewBox is a band of
 * the FIXTURE, and a caller that knows where a part sits in the model can cut
 * that part out of the artwork. `preserveAspectRatio` is dropped to `none` so
 * the band fills the box it is given rather than re-centring inside it.
 */
export const cropSvgViewBox = (svg, crop) => {
  if (!crop || !(crop.height > 0)) return svg;
  const m = svg.match(
    /viewBox="([-\d.eE]+)\s+([-\d.eE]+)\s+([-\d.eE]+)\s+([-\d.eE]+)"/
  );
  const close = svg.lastIndexOf('</svg>');
  if (!m || close < 0) return svg;
  const [, x, y, w, h] = m.slice(0, 5).map(Number);
  const top = y + h * (crop.top || 0);
  const height = h * crop.height;
  const clip = bandClipRect({ x, y, w, h }, crop);
  // Ids are document-wide, and a page draws many pendants. The id is made of
  // the rectangle itself, so two crops sharing one also share its geometry.
  const clipId = `band-clip-${[clip.x, clip.y, clip.width, clip.height]
    .map((v) => v.toFixed(4))
    .join('_')
    .replace(/\./g, 'p')}`;
  // A viewBox is a WINDOW, not a knife: with `overflow="visible"` (which
  // `processSvgContent` forces, so a projection may draw past its box)
  // cropping only RESCALES — the whole fixture lands in the band, squashed to
  // its height. It has to be clipped to be cut, and the clip is the band's
  // own rectangle rather than the window, so the artwork's outer lines keep
  // their full width (`bandClipRect`).
  const opened = svg
    .slice(0, close)
    .replace(m[0], `viewBox="${x} ${top} ${w} ${height}"`)
    .replace(/preserveAspectRatio="[^"]*"/, 'preserveAspectRatio="none"')
    .replace(/<svg([^>]*)\soverflow="[^"]*"/, '<svg$1')
    .replace(
      /<svg([^>]*)>/,
      `<svg$1 overflow="visible"><defs><clipPath id="${clipId}"><rect x="${clip.x}" y="${clip.y}" width="${clip.width}" height="${clip.height}"/></clipPath></defs><g clip-path="url(#${clipId})">`
    );
  return `${opened}</g>${svg.slice(close)}`;
};

/**
 * Apply selection styling to processed SVG (blue color, bold strokes)
 * This is applied after base processing to avoid cache invalidation
 */
const applySelectionStyling = (
  svg,
  isSelected,
  isHighlighted,
  baseStrokeWidth
) => {
  if (!isSelected && !isHighlighted) return svg;

  let styledSvg = svg;
  const strokeWidth = baseStrokeWidth * 2; // Bold: 2x normal width

  // Change color for selected (blue) and highlighted (red) objects
  if (isSelected || isHighlighted) {
    const strokeColor = isSelected ? HIGHLIGHT_COLORS.PRIMARY : '#ef4444';
    styledSvg = styledSvg.replace(
      /stroke="(black|#000000|#000)"/gi,
      `stroke="${strokeColor}"`
    );
    styledSvg = styledSvg.replace(
      /stroke:(black|#000000|#000)/gi,
      `stroke:${strokeColor}`
    );
  }

  // Update stroke width for both selected and highlighted
  styledSvg = styledSvg.replace(
    /stroke-width="[^"]*"/g,
    `stroke-width="${strokeWidth}"`
  );

  return styledSvg;
};

/**
 * Stable style object for the default consumer at the bottom of this file.
 * Exporting so external consumers (group editor) can use the same reference
 * — important because passing a fresh object literal would defeat the
 * `React.memo` wrapper below and cause every projection to re-render on
 * every parent render (e.g. during a drag).
 */
export const INLINE_PROJECTION_STYLE = {
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

/**
 * Component that renders an inline SVG projection.
 * Uses non-scaling-stroke to ensure uniform stroke width across all projections.
 *
 * Wrapped in `React.memo` so unchanged projections (the common case during
 * drag interactions where only the parent transform updates) skip the
 * expensive `dangerouslySetInnerHTML` reconciliation.
 */
const InlineSVGProjectionImpl = ({
  url,
  style,
  alt,
  strokeWidthPx = 1.5,
  isSelected = false,
  isHighlighted = false,
  crop = null,
}) => {
  const [svgContent, setSvgContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    // Check cache first
    if (svgCache.has(url)) {
      setSvgContent(svgCache.get(url));
      setLoading(false);
      return;
    }

    // Fetch SVG with performance tracking
    setLoading(true);
    const fetchStart = performance.now();
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch SVG');
        return response.text();
      })
      .then((text) => {
        const fetchDuration = performance.now() - fetchStart;
        recordSvgFetch(url, fetchDuration);

        svgCache.set(url, text);
        setSvgContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching SVG:', err);
        setError(err);
        setLoading(false);
      });
  }, [url]);

  if (loading || error || !svgContent) {
    // Return placeholder while loading or on error
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(200, 200, 200, 0.2)',
        }}
      >
        {error ? '!' : ''}
      </div>
    );
  }

  // Get processed SVG from cache (or process and cache it)
  let finalSvg = processSvgContent(svgContent, strokeWidthPx, url);

  // Apply selection/highlight styling (blue color, bold strokes)
  finalSvg = applySelectionStyling(
    finalSvg,
    isSelected,
    isHighlighted,
    strokeWidthPx
  );

  // Drawing one PART of a fixture: crop to its band of the artwork.
  finalSvg = cropSvgViewBox(finalSvg, crop);

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      }}
      dangerouslySetInnerHTML={{ __html: finalSvg }}
      aria-label={alt}
    />
  );
};

export const InlineSVGProjection = React.memo(InlineSVGProjectionImpl);
/** The raw-SVG cache, for consumers that read the markup (occupancy grid). */
export const getSvgCache = () => svgCache;
